/**
 * Comment utility functions for managing comment trees and replies
 */

/**
 * Recursively finds a comment by ID in a comment tree
 * @param {Array} commentsList - Array of comments to search
 * @param {string|number} id - Comment ID to find
 * @returns {Object|null} - Found comment or null
 */
const matchesCommentId = (commentId, targetId) => String(commentId) === String(targetId);

export const findCommentById = (commentsList, id) => {
  if (!id || !Array.isArray(commentsList)) return null;
  for (const comment of commentsList) {
    if (matchesCommentId(comment.id, id)) return comment;
    if (comment.replies?.length) {
      const match = findCommentById(comment.replies, id);
      if (match) return match;
    }
  }
  return null;
};

/**
 * Recursively updates a comment in a comment tree
 * @param {Array} commentsList - Array of comments
 * @param {string|number} id - Comment ID to update
 * @param {Function} updater - Function that receives the comment and returns updated version
 * @returns {Array} - Updated comments array
 */
export const updateCommentInTree = (commentsList, id, updater) => {
  if (!Array.isArray(commentsList)) return commentsList;
  return commentsList.map((comment) => {
    if (matchesCommentId(comment.id, id)) return updater(comment);
    if (comment.replies?.length) {
      const updatedReplies = updateCommentInTree(comment.replies, id, updater);
      if (updatedReplies !== comment.replies) {
        return { ...comment, replies: updatedReplies };
      }
    }
    return comment;
  });
};

/**
 * Recursively removes a comment from a comment tree
 * @param {Array} commentsList - Array of comments
 * @param {string|number} id - Comment ID to remove
 * @returns {Array} - Updated comments array without the removed comment
 */
export const removeCommentFromTree = (commentsList, id) => {
  if (!Array.isArray(commentsList)) return commentsList;
  return commentsList
    .filter((comment) => !matchesCommentId(comment.id, id))
    .map((comment) => {
      if (!comment.replies?.length) return comment;
      const updatedReplies = removeCommentFromTree(comment.replies, id);
      if (updatedReplies !== comment.replies) {
        return { ...comment, replies: updatedReplies };
      }
      return comment;
    });
};

/**
 * Inserts a reply into a comment tree at the appropriate location
 * @param {Array} replies - Array of replies
 * @param {string|number|null} parentReplyId - Parent reply ID (null for top-level reply)
 * @param {Object} replyObj - Reply object to insert
 * @returns {Object} - Object with updated replies array and inserted flag
 */
export const insertReplyIntoTree = (replies = [], parentReplyId, replyObj) => {
  if (!parentReplyId) {
    return { replies: [replyObj, ...(replies || [])], inserted: true };
  }
  let inserted = false;
  const nextReplies = (replies || []).map((reply) => {
    if (matchesCommentId(reply.id, parentReplyId)) {
      inserted = true;
      return { ...reply, replies: [replyObj, ...(reply.replies || [])] };
    }
    if (reply.replies && reply.replies.length > 0) {
      const result = insertReplyIntoTree(reply.replies, parentReplyId, replyObj);
      if (result.inserted) inserted = true;
      return { ...reply, replies: result.replies };
    }
    return reply;
  });
  return { replies: nextReplies, inserted };
};

/**
 * Gets unique reply authors for avatar display
 * @param {Array} replies - Array of replies
 * @param {number} max - Maximum number of authors to return
 * @param {Function} t - Translation function
 * @returns {Array} - Array of unique author objects
 */
export const getReplyAuthors = (replies = [], max = 3, t = (key) => key) => {
  const unique = [];
  const seen = new Set();
  replies.forEach((reply) => {
    const key = reply.author?.name || reply.author?.avatar || '';
    if (!key || seen.has(key)) return;
    seen.add(key);
    unique.push({
      name: reply.author?.name || t('taskViewModal.unknownUser')
    });
  });
  return unique.slice(0, max);
};

/**
 * Gets the reply count label (singular or plural)
 * @param {number} count - Number of replies
 * @param {Function} t - Translation function
 * @returns {string} - Translated reply count label
 */
export const getReplyCountLabel = (count, t = (key) => key) => {
  if (count === 1) {
    return t('taskViewModal.replyCountSingle', { count });
  }
  return t('taskViewModal.replyCountMultiple', { count });
};

/**
 * Checks if a reply thread is open
 * @param {string|number} commentId - Comment ID to check
 * @param {Array} openReplyThreads - Array of open reply thread IDs
 * @returns {boolean} - True if thread is open
 */
export const isReplyThreadOpen = (commentId, openReplyThreads) => {
  return openReplyThreads.includes(commentId);
};

/**
 * Toggles a reply thread open/closed state
 * @param {string|number} commentId - Comment ID to toggle
 * @param {Array} openReplyThreads - Current array of open reply thread IDs
 * @returns {Array} - Updated array of open reply thread IDs
 */
export const toggleReplyThread = (commentId, openReplyThreads) => {
  return openReplyThreads.includes(commentId)
    ? openReplyThreads.filter((id) => id !== commentId)
    : [...openReplyThreads, commentId];
};

/**
 * Checks if a comment is owned by the current user
 * @param {string} commentAuthorName - Comment author's name
 * @param {Object} user - Current user object
 * @returns {boolean} - True if comment is owned by current user
 */
export const isCommentOwnedByUser = (commentAuthorName, user) => {
  const normalize = (value) => (value || '').toLowerCase().replace(/\s+/g, ' ').trim();
  const currentUserName = normalize(`${user?.first_name || ''} ${user?.last_name || ''}`);
  return Boolean(currentUserName) && currentUserName === normalize(commentAuthorName);
};

/**
 * Gets the current user's display information
 * @param {Object} user - Current user object
 * @param {Function} t - Translation function
 * @returns {Object} - Object with name and avatar
 */
export const getCurrentUserDisplay = (user, t = (key) => key) => {
  const userName = `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || t('taskViewModal.unknownUser');
  return {
    name: userName,
    avatar: userName.charAt(0) || t('taskViewModal.unknownUserInitial')
  };
};

/**
 * Formats a date or timestamp string into DD/MM/YYYY format consistently across all operating systems
 * @param {string|number|Date} dateInput - ISO date string, timestamp, or Date instance
 * @returns {string} - Formatted date string in DD/MM/YYYY format
 */
export const formatCommentTimestamp = (dateInput) => {
  if (!dateInput) return '';
  const d = new Date(dateInput);
  if (isNaN(d.getTime())) return '';
  try {
    return d.toLocaleString('en-GB');
  } catch (e) {
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year}, ${hours}:${minutes}`;
  }
};

/**
 * Maps API comment response to internal comment format
 * @param {Object} comment - API comment object
 * @param {Function} t - Translation function
 * @param {Function} decodeHtmlContent - Function to decode HTML content
 * @returns {Object} - Mapped comment object
 */
export const mapApiComment = (comment, t = (key) => key, decodeHtmlContent = (content) => content) => {
  const mapped = {
    id: comment.comment_id,
    author: {
      name: comment.user_name || t('taskViewModal.unknownUser'),
      avatar: comment.user_name?.charAt(0) || t('taskViewModal.unknownUserInitial')
    },
    content: decodeHtmlContent(comment.comment || ''),
    timestamp: formatCommentTimestamp(comment.created_at) || t('taskViewModal.unknownTime'),
    attachments: comment.attachments || [],
    replies: []
  };
  if (Array.isArray(comment.child_comments) && comment.child_comments.length > 0) {
    mapped.replies = comment.child_comments.map((childComment) =>
      mapApiComment(childComment, t, decodeHtmlContent)
    );
  }
  return mapped;
};
